import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    Select,
    Flex,
    Button,
    Box,
    Heading,
    GridItem,
    HStack,
    Input,
} from '@chakra-ui/react';
import CustomVariables from './CustomVariables';
import AvailabilitySchedule from './AvailabilitySchedule';
import { useUserData } from 'context/UserDataProvider';

const TIME_INTERVALS = [
    { value: "", label: 'No buffer' },
    { value: 5, label: '5 minutes' },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
];

const DURATION_OPTIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '60 minutes' },
    { value: 90, label: '90 minutes' },
    { value: 120, label: '120 minutes' },
];

const Settings = ({
    calendarSettings,
    onCalendarSettingsChange,
    onSave,
    isSaving = false,
}) => {
    const { userData } = useUserData();
    // Check for timezone mismatch

    const {
        selectedCalendar,
        timeInterval,
        defaultEventDuration,
        customVariables,
        schedule,
        startTimeIncrements,
        bookingBufferPeriod,
        listOfCalendars,
    } = calendarSettings;
    console.log(calendarSettings);
    const bookingBuffer = bookingBufferPeriod || calendarSettings?.booking_buffer_period || null;
    console.log('Current bookingBuffer:', bookingBuffer);


    const handleCalendarChange = (event) => {
        const newCalendarId = event.target.value;
        const selectedCal = listOfCalendars.find((cal) => cal.id === newCalendarId);

        onCalendarSettingsChange('selectedCalendar', { id: newCalendarId, name: selectedCal ? selectedCal.name : '' });
    };

    function shouldShowTimezoneWarning(userData, selectedCalendar, listOfCalendars) {
        const fullSelectedCal = listOfCalendars.find((cal) => cal.id === selectedCalendar.id);
        const agentTimeZone = userData?.agent_info?.time_zone;
        const calendarTimeZone = fullSelectedCal?.timezone;
        return !!(agentTimeZone && calendarTimeZone && agentTimeZone !== calendarTimeZone);
    }

    const showTimezoneWarning = shouldShowTimezoneWarning(userData, selectedCalendar, listOfCalendars);
    const fullSelectedCal = listOfCalendars.find((cal) => cal.id === selectedCalendar.id);
    const agentTimeZone = userData?.agent_info?.time_zone;
    const calendarTimeZone = fullSelectedCal?.timezone;


    const handleTimeIntervalChange = (event) => {
        onCalendarSettingsChange('timeInterval', parseInt(event.target.value));
    };

    const handleDurationChange = (event) => {
        onCalendarSettingsChange('defaultEventDuration', parseInt(event.target.value));
    };

    const handleSaveClick = () => {
        onSave({
            calendar_id: selectedCalendar.id,
            calendar_name: selectedCalendar.name,
            time_interval: timeInterval,
            default_event_duration: defaultEventDuration,
            custom_variables: customVariables,
            schedule: schedule,
            start_time_increments: startTimeIncrements,
            booking_buffer_period: bookingBufferPeriod,
        });
    };

    const LEAD_TIME_PRESETS = [
        { label: 'None', value: { value: '', unit: 'hours' } },
        { label: '4 Hours', value: { value: 4, unit: 'hours' } },
        { label: '1 Day', value: { value: 1, unit: 'days' } },
        { label: '3 Days', value: { value: 3, unit: 'days' } },
        { label: '1 Week', value: { value: 7, unit: 'days' } },
    ];

    const handlePresetClick = (preset) => {
        onCalendarSettingsChange('bookingBufferPeriod', preset.value);
    };

    const handleCustomValueChange = (e) => {
        const val = parseInt(e.target.value || 0);
        const currentUnit = bookingBufferPeriod?.unit || 'hours';
        onCalendarSettingsChange('bookingBufferPeriod', { value: val, unit: currentUnit });
    };

    const handleCustomUnitChange = (e) => {
        const unit = e.target.value;
        const currentValue = bookingBufferPeriod?.value || null;
        onCalendarSettingsChange('bookingBufferPeriod', { value: currentValue, unit });
    };

    const handleCustomVariablesSave = (v) => {
        onCalendarSettingsChange('customVariables', v);
    };

    const handleScheduleChange = (newSchedule) => {
        onCalendarSettingsChange('schedule', newSchedule);
    };

    return (<>
        {showTimezoneWarning && (
            <Box mb={4}>
                <Box bg="yellow.100" borderRadius="md" p={3} border="1px solid #ECC94B">
                    <Text color="yellow.800" fontWeight="bold">
                        Warning: Your agent's timezone (<b>{agentTimeZone}</b>) does not match the selected calendar's timezone (<b>{calendarTimeZone}</b>). This may cause scheduling issues. Please ensure both are set to the same timezone for accurate event times.
                    </Text>
                </Box>
            </Box>
        )}
        <Box mb={6}>
            <Heading as="h2" size="lg" fontWeight="bold" mb={1}>
                Google Calendar Settings
            </Heading>
            <Text color="gray.600" fontSize="sm">
                Configure your calendar preferences, availability, and custom variables for scheduling.
            </Text>
        </Box>
        <Flex gap={6} flexDirection={{ base: 'column', md: 'row' }} width='100%'>
            {/* Left column: calendar, buffer, custom variables */}
            <Box flex={1} minW={0}>
                {/* Calendar Selection */}
                <Box mb={6}>
                    <Text mb={2}>Select the calendar to use for scheduling:</Text>
                    {listOfCalendars && listOfCalendars.length > 0 ? (
                        <Select
                            placeholder='Please choose calendar'
                            value={selectedCalendar.id}
                            onChange={handleCalendarChange}
                            isDisabled={isSaving}
                        >
                            {listOfCalendars.map((calendar) => (
                                <option key={calendar.id} value={calendar.id}>
                                    {calendar.name}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        <Text color='gray.500'>No calendars found or unable to load calendars.</Text>
                    )}
                    {selectedCalendar.name && (
                        <Flex align='center' mt={2}>
                            <Text fontWeight='medium' mr={2}>
                                Selected Calendar:
                            </Text>
                            <Text color='blue.600'>{selectedCalendar.name}</Text>
                        </Flex>
                    )}
                </Box>
                {/* Buffer Between Events */}
                <Box mb={6}>
                    <Text mb={2}>Buffer Between Events</Text>
                    <Select
                        value={timeInterval}
                        onChange={handleTimeIntervalChange}
                        isDisabled={isSaving}
                    >
                        {TIME_INTERVALS.map((interval) => (
                            <option key={interval.value} value={interval.value}>
                                {interval.label}
                            </option>
                        ))}
                    </Select>
                </Box>
                {/* Scheduling Lead Time */}
                <Box mb={6}>
                    <Text mb={2}>Scheduling Lead Time</Text>
                    <Text color='gray.600' fontSize='sm' mb={2}>
                        Set the minimum amount of notice required before an appointment can be booked.
                    </Text>
                    <HStack spacing={2} mb={3}>
                        {LEAD_TIME_PRESETS.map((preset) => (
                            <Button
                                key={preset.label}
                                size='sm'
                                variant={bookingBuffer && bookingBuffer.value === preset.value.value && bookingBuffer.unit === preset.value.unit ? 'solid' : 'outline'}
                                onClick={() => handlePresetClick(preset)}
                                isDisabled={isSaving}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </HStack>
                    <HStack spacing={2} align='center'>
                        <Input
                            type='number'
                            min={0}
                            value={bookingBuffer?.value ?? ''}
                            onChange={handleCustomValueChange}
                            placeholder='Custom'
                            size='md'
                            isDisabled={isSaving}
                            width='80px'
                        />
                        <Select value={bookingBuffer?.unit ?? 'hours'} onChange={handleCustomUnitChange} size='sm' width='110px' isDisabled={isSaving}>
                            <option value='hours'>Hours</option>
                            <option value='days'>Days</option>
                        </Select>
                    </HStack>
                </Box>
                {/* Custom Variables Section */}
                <Box mb={6}>
                    <CustomVariables
                        customVariables={customVariables}
                        onSave={handleCustomVariablesSave}
                    />
                </Box>
                {/* Default Event Duration */}
                <Box mb={6}>
                    <Text mb={2}>Default Event Duration</Text>
                    <Select
                        value={defaultEventDuration}
                        onChange={handleDurationChange}
                        isDisabled={isSaving}
                    >
                        {DURATION_OPTIONS.map((duration) => (
                            <option key={duration.value} value={duration.value}>
                                {duration.label}
                            </option>
                        ))}
                    </Select>
                </Box>
                {/* Save Button */}
                <Flex justify='flex-start' mt={4}>
                    <Button
                        colorScheme='blue'
                        onClick={handleSaveClick}
                        isLoading={isSaving}
                        size='sm'
                    >
                        Save Settings
                    </Button>
                </Flex>
            </Box>
            {/* Right column: duration, availability, save */}
            <Box flex={1} minW={0}>

                {/* Availability Schedule Section */}
                <Box mb={6}>
                    <AvailabilitySchedule
                        schedule={schedule}
                        onScheduleChange={handleScheduleChange}
                    />
                </Box>

            </Box>
        </Flex>
    </>
    );
};

export default Settings;
